<template>
  <teleport to="body">
    <AppModal
      :isVisible="isVisible"
      :title="isEditMode ? $t('finance.editExpense') : $t('finance.recordExpense')"
      @cancel="handleCancel"
    >
      <form @submit.prevent="handleSubmit" class="finance-form">
        <div class="form-group">
          <label>{{ $t('finance.amount') }}</label>
          <input
            type="number"
            v-model="formData.amount"
            step="0.01"
            required
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.paymentType') }}</label>
          <select v-model="formData.paymentType" required class="form-input">
            <option value="">{{ $t('finance.selectPaymentType') }}</option>
            <option value="CASH">{{ $t('finance.paymentTypes.cash') }}</option>
            <option value="BANK_TRANSFER">{{ $t('finance.paymentTypes.bankTransfer') }}</option>
            <option value="CHECK">{{ $t('finance.paymentTypes.check') }}</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.intervenant') }} *</label>
          <select v-model="formData.intervenantId" class="form-input" required @change="handleIntervenantChange">
            <option value="">{{ $t('finance.selectIntervenant') }}</option>
            <option v-for="intervenant in intervenants" :key="intervenant.id" :value="intervenant.id">
              {{ intervenant.name }}
            </option>
          </select>
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.task') }}</label>
          <select v-model="formData.taskId" class="form-input" :disabled="!formData.intervenantId">
            <option value="">{{ $t('finance.selectTask') }}</option>
            <option v-for="task in filteredTasks" :key="task.id" :value="task.id">
              {{ task.name }}
            </option>
          </select>
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.transactionDate') }}</label>
          <input
            type="date"
            v-model="formData.transactionDate"
            required
            class="form-input"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.description') }}</label>
          <textarea
            v-model="formData.description"
            class="form-input"
          ></textarea>
        </div>
        
        <div class="form-group">
          <label>{{ $t('finance.invoice') }}</label>
          <input
            type="file"
            @change="handleInvoiceUpload"
            accept=".pdf,.jpg,.jpeg,.png"
            class="form-input"
          />
        </div>
        
        <div class="form-actions">
          <AppButton type="button" variant="outline" @click="handleCancel">
            {{ $t('common.cancel') }}
          </AppButton>
          <AppButton type="submit" variant="primary">
            {{ $t('common.save') }}
          </AppButton>
        </div>
      </form>
    </AppModal>
  </teleport>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, watch, PropType, computed } from 'vue';
import AppButton from '@/components/base/AppButton.vue';
import AppModal from '@/components/base/Modal.vue';
import type { ExpenseData, Intervenant } from '@/types/services';

interface TaskWithIntervenant {
  id: string;
  name: string;
  intervenantId?: string;
  intervenantName: string;
  [key: string]: any;
}

const props = defineProps({
  isVisible: { type: Boolean as PropType<boolean>, default: false },
  tasks: { type: Array as PropType<TaskWithIntervenant[]>, default: () => [] },
  intervenants: { type: Array as PropType<Intervenant[]>, default: () => [] },
  expense: { type: Object as PropType<ExpenseData | null>, default: null },
  isEditMode: { type: Boolean as PropType<boolean>, default: false }
});

interface Emits {
  (e: 'cancel'): void;
  (e: 'submit', payload: { data: FormDataType; isValid: boolean }): void;
}

interface FormDataType {
  amount: number | null;
  description: string;
  paymentType: string;
  transactionDate: string;
  invoice: File | null;
  intervenantId: string | null;
  taskId: string | null;
}

const emit = defineEmits<Emits>();

// Form data
const formData = ref<FormDataType>({
  amount: null,
  description: '',
  paymentType: '',
  transactionDate: new Date().toISOString().split('T')[0],
  invoice: null,
  intervenantId: null,
  taskId: null
});

// Computed property to filter tasks by selected intervenant
const filteredTasks = computed(() => {
  if (!formData.value.intervenantId) {
    return [];
  }
  return props.tasks.filter((task: TaskWithIntervenant) => task.intervenantId === formData.value.intervenantId);
});

// Methods
const handleIntervenantChange = () => {
  // Clear task selection when intervenant changes
  formData.value.taskId = null;
};

const handleCancel = () => {
  resetForm();
  emit('cancel');
};

const handleSubmit = () => {
  // Validate required fields
  if (!formData.value.amount || !formData.value.paymentType || !formData.value.intervenantId || !formData.value.transactionDate) {
    // Parent component should handle validation error display
    emit('submit', { data: formData.value, isValid: false });
    return;
  }
  
  emit('submit', { data: formData.value, isValid: true });
};

const handleInvoiceUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  formData.value.invoice = target.files?.[0] || null;
};

const resetForm = () => {
  if (props.isEditMode && props.expense) {
    // Initialize with existing expense data for editing
    formData.value = {
      amount: props.expense.amount || null,
      description: props.expense.description || '',
      paymentType: props.expense.paymentType || '',
      transactionDate: props.expense.transactionDate || new Date().toISOString().split('T')[0],
      invoice: null, // File input will be empty for editing
      intervenantId: props.expense.intervenantId || null,
      taskId: props.expense.taskId || null
    };
  } else {
    // Initialize with empty data for new expense
    formData.value = {
      amount: null,
      description: '',
      paymentType: '',
      transactionDate: new Date().toISOString().split('T')[0],
      invoice: null,
      intervenantId: null,
      taskId: null
    };
  }
  
  // Reset file input
  const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
  if (fileInput) {
    fileInput.value = '';
  }
};

// Watch for visibility changes to reset form when modal is opened
watch(() => props.isVisible, (newVal: boolean) => {
  if (newVal) {
    resetForm();
  }
});

// Watch for expense changes to populate form when editing
watch(() => props.expense, (newExpense: any) => {
  if (props.isEditMode && newExpense) {
    resetForm();
  }
});
</script>

<style scoped>
.finance-form {
  padding: var(--space-md);
}

.form-group {
  margin-bottom: var(--space-md);
}

.form-group label {
  display: block;
  margin-bottom: var(--space-xs);
  font-weight: 500;
  color: var(--text-primary);
}

.form-input {
  width: 100%;
  padding: var(--space-sm);
  border: 1px solid var(--neutral-300);
  border-radius: var(--border-radius-sm);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm);
  margin-top: var(--space-lg);
}
</style>
